// src/components/notifications/NotificationPanel.tsx
//
// The bell's popover card (Story 4.9). A scrim-less, absolutely-positioned
// standard `.card` under the bell (Decision #9 — one layer; a dimming overlay
// would overweight low-noise notifications). Esc closes, click-outside closes,
// and focus returns to the bell on close (the onClose handler owns focus
// restore). It renders the caller's 20 most recent in_app notifications
// newest-first, read + unread together, with unread distinguished by MORE than
// colour (brand-600 dot + medium weight + an sr-only "Unread" label). No
// pagination, no infinite scroll (banned) — a "Showing the 20 most recent." meta
// line is the honesty valve when the window is full.

import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import type { YakNotificationWithTicket } from '../../types/yak'
import { BELL_LIST_LIMIT } from '../../services/notificationsService'
import {
  bellEntryOrFallback,
  bellTarget,
  isBellRenderable,
} from '../../utils/notificationDisplay'
import { questionTypeLabel } from '../../utils/ticketDisplay'
import { formatRelativeTime, formatDateTime } from '../../utils/dateUtils'

interface NotificationPanelProps {
  notifications: YakNotificationWithTicket[]
  unreadCount: number
  markingAll: boolean
  onMarkRead: (id: string) => void
  onMarkAllRead: () => void
  onClose: () => void
  // The bell button, so an outside-click on the bell itself does not close then
  // immediately re-open, and focus restore has a target.
  anchorRef: React.RefObject<HTMLElement>
}

export function NotificationPanel({
  notifications,
  unreadCount,
  markingAll,
  onMarkRead,
  onMarkAllRead,
  onClose,
  anchorRef,
}: NotificationPanelProps) {
  const navigate = useNavigate()
  const panelRef = useRef<HTMLDivElement>(null)

  // Esc closes; a document mousedown outside the card AND the bell closes. Both
  // are cleaned up on unmount.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (panelRef.current?.contains(target)) return
      if (anchorRef.current?.contains(target)) return
      onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('mousedown', onMouseDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('mousedown', onMouseDown)
    }
  }, [onClose, anchorRef])

  const renderable = notifications.filter(n => isBellRenderable(n.type))

  const handleItemClick = (n: YakNotificationWithTicket) => {
    // Mark read on click (set-once server-side); then navigate to the item's
    // surface if it has one and close the panel. A row with no target marks read
    // and leaves the panel open (AC 3).
    onMarkRead(n.id)
    const target = bellTarget(n)
    if (target) {
      navigate(target)
      onClose()
    }
  }

  return (
    <div
      ref={panelRef}
      className="card absolute right-0 top-full mt-2 w-80 max-w-[calc(100vw-2rem)] z-20"
      aria-label="Notifications"
    >
      <div className="card-header flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-900">Notifications</h2>
        {unreadCount > 0 && (
          <button
            type="button"
            onClick={onMarkAllRead}
            disabled={markingAll}
            className="text-xs font-medium text-brand-600 hover:text-brand-700 disabled:opacity-50"
          >
            Mark all read
          </button>
        )}
      </div>

      {renderable.length === 0 ? (
        <div className="card-body text-sm text-gray-500">No notifications.</div>
      ) : (
        <div className="max-h-96 overflow-y-auto divide-y divide-gray-100">
          {renderable.map(n => {
            const isUnread = n.read_at === null
            const title = bellEntryOrFallback(n.type).title
            const created = n.created_at
            return (
              <button
                key={n.id}
                type="button"
                onClick={() => handleItemClick(n)}
                className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors focus:outline-none focus:bg-gray-50"
              >
                <div className="flex items-start gap-2">
                  {isUnread && (
                    <span
                      aria-hidden="true"
                      className="mt-1.5 w-2 h-2 flex-shrink-0 rounded-full bg-brand-600"
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <div
                      className={
                        isUnread
                          ? 'text-sm font-medium text-gray-900'
                          : 'text-sm text-gray-600'
                      }
                    >
                      {isUnread && <span className="sr-only">Unread. </span>}
                      {title}
                    </div>
                    {n.ticket && (
                      <div className="mt-0.5 text-xs text-gray-500 truncate">
                        {n.ticket.question}
                        <span className="text-gray-400">
                          {' · '}
                          {questionTypeLabel(n.ticket.question_type)}
                        </span>
                      </div>
                    )}
                    {created && (
                      <time
                        dateTime={created}
                        title={formatDateTime(created)}
                        className="mt-0.5 block text-xs text-gray-400"
                      >
                        {formatRelativeTime(created)}
                      </time>
                    )}
                  </div>
                </div>
              </button>
            )
          })}
        </div>
      )}

      {renderable.length >= BELL_LIST_LIMIT && (
        <div className="card-footer text-xs text-gray-400">
          Showing the 20 most recent.
        </div>
      )}
    </div>
  )
}
